import { RELEASE_VERSION_FILE } from "constants/release"
import { useEffect, useState } from "react"

import { Duration } from "@shared/types/duration"

/** How often an open tab checks whether a newer release was deployed */
export const RELEASE_CHECK_INTERVAL = Duration.minutes(1).milliseconds

/**
 * Fetches the release that is deployed right now from the {@link RELEASE_VERSION_FILE} the build emits.
 *
 * Resolves to `undefined` when that can't be determined: offline, halfway through a deploy, or in
 * a build without the file. Failing is harmless, the next check simply tries again.
 */
export const fetchDeployedRelease = async (): Promise<string | undefined> => {
  try {
    // `no-store` skips the browser cache, which would otherwise keep the file for up to 5 minutes
    const response = await fetch(`${import.meta.env.BASE_URL}${RELEASE_VERSION_FILE}`, { cache: "no-store" })
    if (!response.ok) {
      return undefined
    }

    const { release } = (await response.json()) as { release?: unknown }
    return typeof release === "string" && release ? release : undefined
  } catch {
    return undefined
  }
}

/**
 * Returns the deployed release once it differs from the release this bundle was built for, so the
 * user can be offered a refresh. An open tab keeps running the JavaScript it loaded, against a
 * backend that may have moved on.
 *
 * Checks on mount, every {@link RELEASE_CHECK_INTERVAL} while the tab is visible, and whenever it
 * becomes visible again. Never checks in a build without a release name (local and e2e).
 */
export const useNewReleaseAvailable = (): string | undefined => {
  const [deployedRelease, setDeployedRelease] = useState<string>()

  useEffect(() => {
    const bundledRelease = import.meta.env.VITE_RELEASE_NAME
    if (!bundledRelease) {
      return
    }

    let isUnmounted = false

    const checkForNewRelease = async () => {
      if (document.visibilityState === "hidden") {
        return
      }

      const release = await fetchDeployedRelease()
      if (!isUnmounted && release && release !== bundledRelease) {
        setDeployedRelease(release)
      }
    }

    const check = () => void checkForNewRelease()

    // The browser may keep `index.html` for up to 5 minutes, so even a freshly loaded page can
    // run an older bundle than the one deployed
    check()

    const interval = setInterval(check, RELEASE_CHECK_INTERVAL)
    document.addEventListener("visibilitychange", check)

    return () => {
      isUnmounted = true
      clearInterval(interval)
      document.removeEventListener("visibilitychange", check)
    }
  }, [])

  return deployedRelease
}
